
/**
 * ImageZoomPan
 */

import Hammer from "hammerjs";

class ImageZoomPan {
	/**
	 * constructor
	 *
	 * @param {Element} img
	 * @param {Object} [options]
	 *        {Number} [options.initialScale]
	 *        {Number} [options.minScale]
	 *        {Number} [options.maxScale]
	 */

	constructor(img, options = {}) {
		const { initialScale = 1, minScale = 1, maxScale = 8 } = options;

		if (img && img.parentElement) {
			this.img = img;
			this.container;

			this.createElementsAndStyles();

			this.scale = initialScale;
			this.minScale = minScale;
			this.maxScale = maxScale;
			this.lastScale = this.scale;

			this.imgHeight = this.img.height;
			this.imgWidth = this.img.width;
	
			this.viewportHeight = this.container.offsetHeight;
			this.viewportWidth = this.container.offsetWidth;
	
			this.curHeight = this.imgHeight * this.scale;
			this.curWidth = this.imgWidth * this.scale;

			this.x = 0;
			this.y = 0;
			this.lastX = 0;
			this.lastY = 0;
			this.pinchCenter = null;

			this.addEvents();
			this.initHammer();

			this.translate(0, 0);

			this.img.ImageZoomPan = this;
		}
	}

	/**
	 * createElementsAndStyles
	 */
	createElementsAndStyles() {
		this.container = document.createElement("div");
		this.container.classList.add("image-zoom-pan-container");

		this.img.parentElement.appendChild(this.container);

		this.container.appendChild(this.img);

		this.img.classList.add("image-zoom-pan-image");
		this.img.style.transformOrigin = "0 0 0";
	}

	/**
	 * addEvents
	 */
	addEvents() {
		this.container.addEventListener(
			"touchstart",
			function (e) {
				e.preventDefault();
			},
			false
		);

		this.container.addEventListener(
			"touchmove",
			function (e) {
				e.preventDefault();
			},
			false
		);
	}

	/**
	 * initHammer
	 */
	initHammer() {
		const mc = new Hammer(this.container);

		mc.get("pinch").set({ enable: true });

		mc.on("pan panend pinch pinchend doubletap", e => {
			this.calculateSizes();
			switch (e.type) {
				case "pan":
					this.translate(e.deltaX, e.deltaY);
					break;

				case "panend":
					this.updateLastPos();
					break;

				case "pinch":
					this.pinch(e);
					break;

				case "pinchend":
					this.updateLastScale();
					this.updateLastPos();
					this.clearPinchCenter();
					break;

				case "doubletap":
					this.zoomCenter(this.scale > this.minScale ? 0 : 2);
					break;
			}
		});
	}

	/**
	 * Update viewport and image sizes.
	 */
	calculateSizes() {

		this.imgHeight = this.img.height;
		this.imgWidth = this.img.width;

		this.viewportHeight = this.container.offsetHeight;
		this.viewportWidth = this.container.offsetWidth;

		this.curHeight = this.imgHeight * this.scale;
		this.curWidth = this.imgWidth * this.scale;

	}

	/**
	 * translate
	 *
	 * @param {Number} deltaX
	 * @param {Number} deltaY
	 */
	translate(deltaX, deltaY) {
		// We restrict to the min of the viewport width/height or current width/height
		// as the current width/height may be smaller than the viewport width/height
		this.x =
			this.scale > this.minScale
				? this.restrictRawPos(
					this.lastX + deltaX / this.scale,
					Math.min(this.viewportWidth, this.curWidth),
					this.imgWidth
				)
				: 0;
		this.y =
			this.scale > this.minScale
				? this.restrictRawPos(
					this.lastY + deltaY / this.scale,
					Math.min(this.viewportHeight, this.curHeight),
					this.imgHeight
				)
				: 0;

		const posX = Math.ceil(this.x * this.scale);
		const posY = Math.ceil(this.y * this.scale);

		this.img.style.transform = `translate3d(${posX}px, ${posY}px, 0) scale3d(${this.scale
		}, ${this.scale}, 1)`;
	}

	/**
	 * pinch
	 *
	 * @param {Object} event
	 */
	pinch(event) {
		// We only calculate the pinch center on the first pinch event
		// as we want the center to stay consistent during the entire pinch
		const pinchCenterOffset = { x: 0, y: 0 };

		if (this.pinchCenter === null) {
			this.pinchCenter = this.rawCenter(event);

			pinchCenterOffset.x =
				this.pinchCenter.x * this.scale -
				(-this.x * this.scale +
					Math.min(this.viewportWidth, this.curWidth) / 2);
			pinchCenterOffset.y =
				this.pinchCenter.y * this.scale -
				(-this.y * this.scale +
					Math.min(this.viewportHeight, this.curHeight) / 2);
		}

		// When the user pinch zooms, user expects the pinch center to remain in the same
		// relative location of the screen. To achieve this, the raw zoom center is calculated by
		// first storing the pinch center and the scaled offset to the current center of the image.
		// The new scale is then used to calculate the zoom center. This has the effect of
		// actually translating the zoom center on each pinch zoom event.
		const newScale = this.restrictScale(this.scale * event.scale);
		const zoomX = this.pinchCenter.x * newScale - pinchCenterOffset.x;
		const zoomY = this.pinchCenter.y * newScale - pinchCenterOffset.y;
		const zoomCenter = {
			x: zoomX / newScale,
			y: zoomY / newScale
		};

		this.zoomAround(event.scale, zoomCenter.x, zoomCenter.y, true);
	}

	/**
	 * zoomAround
	 *
	 * @param {Number} scaleBy
	 * @param {Number} rawZoomX
	 * @param {Number} rawZoomY
	 * @param {Boolean} [doNotUpdateLasts]
	 */
	zoomAround(scaleBy, rawZoomX, rawZoomY, doNotUpdateLasts) {
		// Zoom
		this.zoom(scaleBy);

		// New raw center of viewport
		const rawCenterX =
			-this.x + Math.min(this.viewportWidth, this.curWidth) / 2 / this.scale;
		const rawCenterY =
			-this.y + Math.min(this.viewportHeight, this.curHeight) / 2 / this.scale;

		// Delta
		const deltaX = (rawCenterX - rawZoomX) * this.scale;
		const deltaY = (rawCenterY - rawZoomY) * this.scale;

		// Translate back to zoom center
		this.translate(deltaX, deltaY);

		if (!doNotUpdateLasts) {
			this.updateLastScale();
			this.updateLastPos();
		}
	}

	/**
	 * zoom
	 *
	 * @param {Number} scaleBy
	 *
	 * @return {ImageZoomPan} - this
	 */
	zoom(scaleBy) {
		this.scale = this.restrictScale(this.lastScale * scaleBy);

		this.triggerCustomEvent("zoomChange", {
			previousZoom: this.lastScale,
			currentZoom: this.scale
		});

		this.curWidth = this.imgWidth * this.scale;
		this.curHeight = this.imgHeight * this.scale;

		// Adjust margins to make sure that we aren't out of bounds
		this.translate(0, 0);

		return this;
	}

	/**
	 * zoomCenter
	 *
	 * @param {Number} scaleBy
	 *
	 * @return {ImageZoomPan} - this
	 */
	zoomCenter(scaleBy) {
		// Center of viewport
		const zoomX =
			-this.x + Math.min(this.viewportWidth, this.curWidth) / 2 / this.scale;
		const zoomY =
			-this.y + Math.min(this.viewportHeight, this.curHeight) / 2 / this.scale;

		this.zoomAround(scaleBy, zoomX, zoomY);

		return this;
	}

	/**
	 * zoomIn
	 *
	 * @return {ImageZoomPan} - this
	 */
	zoomIn() {
		this.zoomCenter(2);

		return this;
	}

	/**
	 * zoomOut
	 *
	 * @return {ImageZoomPan} - this
	 */
	zoomOut() {
		this.zoomCenter(1 / 2);

		return this;
	}

	/**
	 * rawCenter
	 *
	 * @param {Object} event
	 *
	 * @return {Object}
	 */
	rawCenter(event) {
		const pos = this.container.getBoundingClientRect();
		const zoomX = -this.x + (event.center.x - pos.left) / this.scale;
		const zoomY = -this.y + (event.center.y - pos.top) / this.scale;

		return { x: zoomX, y: zoomY };
	}

	/**
	 * restrictRawPos
	 *
	 * @param {Number} pos
	 * @param {Number} viewportDim
	 * @param {Number} imgDim
	 *
	 * @return {Number}
	 */
	restrictRawPos(pos, viewportDim, imgDim) {
		if (pos < viewportDim / this.scale - imgDim) {
			// too far left/up?
			pos = viewportDim / this.scale - imgDim;
		} else if (pos > 0) {
			// too far right/down?
			pos = 0;
		}

		return pos;
	}

	/**
	 * restrictScale
	 *
	 * @param {Number} scale
	 *
	 * @return {Number}
	 */
	restrictScale(scale) {
		if (scale < this.minScale) {
			scale = this.minScale;
		} else if (scale > this.maxScale) {
			scale = this.maxScale;
		}

		return scale;
	}

	/**
	 * updateLastPos
	 */
	updateLastPos() {
		this.lastX = this.x;
		this.lastY = this.y;
	}

	/**
	 * updateLastScale
	 */
	updateLastScale() {
		this.lastScale = this.scale;
	}

	/**
	 * clearPinchCenter
	 */
	clearPinchCenter() {
		this.pinchCenter = null;
	}

	/**
	 * triggerCustomEvent
	 *
	 * @param {String} name
	 * @param {Object|String} [detail]
	 */
	triggerCustomEvent(name, detail = null) {
		let event;

		if (typeof window.CustomEvent === "function") {
			event = new CustomEvent(name, { detail: detail });
		} else {
			event = document.createEvent("CustomEvent");
			event.initCustomEvent(name, false, false, detail);
		}

		this.img.dispatchEvent(event);
	}
}

export default ImageZoomPan;